import type { ChatMessage } from '@/machines/chatbotMachine'
import type { ChatAttachedFile, ChatMessageAttachment, ChatSession } from '@/services/api/domains/chatbot.api'

/**
 * Maps a persisted `ChatSession.messages[]` array into the `ChatMessage[]`
 * shape the `RESUME_SESSION` event expects, for restoring a resumed
 * conversation on mount.
 *
 * Extracted from `DashboardChatbot`'s own inline resume mapping (its
 * `getActiveSession().then()` handler) so the sitewide assistant runtime
 * (`AssistantProvider`, task 14) can reuse the exact same subtle behaviour
 * without duplicating it by hand — this function's body is unchanged from
 * that origin. `DashboardChatbot` keeps its own copy inline (task-14-brief.md
 * explicitly protects it from edits); this is the shared version for any
 * NEW resume consumer. Do not change this function's behaviour, and do not
 * loosen the filter described below, without re-checking both call sites.
 *
 * The server persists (and returns) `tool_result` rows too — the model needs
 * the full sequence on rebuild — and every tool-bearing turn ALSO persists a
 * companion `{role:'assistant', content:'', toolCalls}` row (the assistant's
 * tool_use step) immediately before its `tool_result` rows. Neither becomes
 * its own bubble here: a `tool_result` row has no UI, and the empty-content
 * assistant row would render as an empty grey bubble. Both rows survive into
 * `session.messages` purely for the provider-history rebuild.
 *
 * Their DATA isn't thrown away, though — it's threaded onto the turn's
 * closing text message (the very next surviving row), so a resumed turn's
 * `ChatMessage` ends up with the same `{content, toolCalls}` shape a LIVE
 * turn gets. `pendingToolCalls` carries the companion row's `{id, name,
 * input}` list forward; each `result` is resolved from the matching
 * `tool_result` row by `toolCallId` (that row's `content` is the only place
 * the executed result — including any geometry `ChatbotMessage`'s
 * click-to-zoom needs — survives persistence; the companion row's own
 * `toolCalls` never carries a `result`).
 */
export function mapSessionMessagesForResume(messages: ChatSession['messages'] | undefined): ChatMessage[] {
  const restored: ChatMessage[] = []
  let pendingToolCalls: ChatMessage['toolCalls']

  for (const m of messages ?? []) {
    if (m.role === 'tool_result') {
      const entry = pendingToolCalls?.find(tc => tc.id === m.toolCallId)

      if (entry) {
        try {
          entry.result = JSON.parse(m.content)
        } catch {
          // Malformed/legacy content — leave the result unresolved rather
          // than fail the whole resume.
        }
      }

      continue
    }

    if (m.content === '') {
      if (m.role === 'assistant' && m.toolCalls && m.toolCalls.length > 0) {
        pendingToolCalls = m.toolCalls.map(tc => ({ ...tc, result: undefined }))
      } else {
        // An empty-content row that ISN'T itself introducing a new tool-call
        // stash (e.g. a tool-bearing turn's closing assistant row that ended
        // with no final text) must not leave a stale `pendingToolCalls` alive
        // — otherwise it attaches to the NEXT turn's restored message instead
        // of being dropped with this row.
        pendingToolCalls = undefined
      }

      continue
    }

    restored.push({
      id: `restored-${restored.length}-${m.timestamp}`,
      role: m.role,
      content: m.content,
      timestamp: new Date(m.timestamp).getTime(),
      ...(m.image ? { image: m.image } : {}),
      ...restoredAttachments(m),
      ...(pendingToolCalls ? { toolCalls: pendingToolCalls } : {}),
    })
    pendingToolCalls = undefined
  }

  return restored
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

/** An `<img src>` the transcript will load: http(s), same-origin path, or an image data URL. */
function isSafeImageSrc(value: unknown): value is string {
  if (typeof value !== 'string') return false

  return (
    value.startsWith('https://') ||
    value.startsWith('http://') ||
    (value.startsWith('/') && !value.startsWith('//')) ||
    value.startsWith('data:image/')
  )
}

/**
 * The picker's markers on a stored message, kept only where their shape is what the transcript can
 * draw. Tolerant on purpose: a session written before attachments existed has neither field, and a
 * malformed entry is dropped rather than failing the whole resume.
 */
function restoredAttachments(m: ChatSession['messages'][number]): Pick<ChatMessage, 'attachments' | 'attachedFiles'> {
  const attachments: ChatMessageAttachment[] = []

  for (const a of Array.isArray(m.attachments) ? (m.attachments as unknown[]) : []) {
    if (!isRecord(a)) continue

    if (a.kind === 'image') {
      // The server returns a stored picture as a URL. Without a usable one the picture still shows as
      // a marker, just without its thumbnail.
      attachments.push(isSafeImageSrc(a.url) ? { kind: 'image', src: a.url } : { kind: 'image' })
    } else if (a.kind === 'document' && typeof a.name === 'string') {
      attachments.push({ kind: 'document', name: a.name })
    }
  }

  const attachedFiles: ChatAttachedFile[] = []

  for (const f of Array.isArray(m.attachedFiles) ? (m.attachedFiles as unknown[]) : []) {
    if (!isRecord(f) || typeof f.id !== 'string') continue
    attachedFiles.push({
      id: f.id,
      title: typeof f.title === 'string' ? f.title : f.id,
      fileType: typeof f.fileType === 'string' ? f.fileType : '',
    })
  }

  return {
    ...(attachments.length ? { attachments } : {}),
    ...(attachedFiles.length ? { attachedFiles } : {}),
  }
}
